"use client"

import { useState } from "react"
import { ChevronDown, ChevronUp, Trash2, AlertCircle } from "lucide-react"
import TerritoryInput from "./TerritoryInput"
import type { Territory } from "../types"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface MobileTerritoryCardsProps {
  territories: Territory[]
  onTerritoryChange: (id: number, field: keyof Territory, value: string | string[]) => void
  onDeleteTerritory: (id: number) => void
  mapMode: "us" | "world"
  showTerritoryColumn: boolean
}

export default function MobileTerritoryCards({
  territories,
  onTerritoryChange,
  onDeleteTerritory,
  mapMode,
  showTerritoryColumn,
}: MobileTerritoryCardsProps) {
  const [expandedCard, setExpandedCard] = useState<number | null>(null)
  const [territoryToDelete, setTerritoryToDelete] = useState<number | null>(null)

  const toggleCard = (id: number) => {
    setExpandedCard((prev) => (prev === id ? null : id))
  }

  const confirmDelete = (id: number) => {
    setTerritoryToDelete(id)
  }

  const handleDelete = () => {
    if (territoryToDelete !== null) {
      onDeleteTerritory(territoryToDelete)
      setTerritoryToDelete(null)
    }
  }

  const cancelDelete = () => {
    setTerritoryToDelete(null)
  }

  return (
    <div className="space-y-4">
      {territories.map((territory) => {
        const isExpanded = expandedCard === territory.id
        return (
          <div
            key={territory.id}
            className={`border rounded-md shadow-sm p-3 bg-white transition-all duration-200 ${
              isExpanded ? "border-blue-200 shadow-md" : ""
            }`}
          >
            {/* Header row */}
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => toggleCard(territory.id)}
              aria-expanded={isExpanded}
              aria-controls={`territory-content-${territory.id}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: territory.color }} />
                <div>
                  <span className="font-medium text-gray-800">{territory.name || "(Untitled)"}</span>
                  <div className="text-xs text-gray-500">
                    {territory.territories.length} {territory.territories.length === 1 ? "region" : "regions"}
                  </div>
                </div>
              </div>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </div>

            {/* Expanded content */}
            <div
              id={`territory-content-${territory.id}`}
              className={`overflow-hidden transition-all duration-300 ${
                isExpanded ? "max-h-[500px] mt-3 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="space-y-3">
                <div>
                  <label htmlFor={`name-${territory.id}`} className="block text-sm text-gray-600 mb-1">
                    Name
                  </label>
                  <input
                    id={`name-${territory.id}`}
                    type="text"
                    value={territory.name}
                    onChange={(e) => onTerritoryChange(territory.id, "name", e.target.value)}
                    className="w-full border rounded px-2 py-1"
                  />
                </div>

                {showTerritoryColumn && (
                  <div>
                    <label htmlFor={`territory-${territory.id}`} className="block text-sm text-gray-600 mb-1">
                      Territory
                    </label>
                    <input
                      id={`territory-${territory.id}`}
                      type="text"
                      value={territory.territory || ""}
                      onChange={(e) => onTerritoryChange(territory.id, "territory", e.target.value)}
                      className="w-full border rounded px-2 py-1"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor={`geography-${territory.id}`} className="block text-sm text-gray-600 mb-1">
                    Geography
                  </label>
                  <TerritoryInput
                    value={territory.territories}
                    onChange={(newVal) => onTerritoryChange(territory.id, "territories", newVal)}
                    mapMode={mapMode}
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div>
                    <label htmlFor={`color-${territory.id}`} className="block text-sm text-gray-600 mb-1">
                      Color
                    </label>
                    <input
                      id={`color-${territory.id}`}
                      type="color"
                      value={territory.color}
                      onChange={(e) => onTerritoryChange(territory.id, "color", e.target.value)}
                      className="w-8 h-8 p-0 border rounded"
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      confirmDelete(territory.id)
                    }}
                    className="text-red-600 text-sm px-3 py-2 rounded hover:bg-red-50 transition-colors"
                    aria-label={`Delete ${territory.name}`}
                  >
                    <Trash2 className="w-4 h-4 inline mr-1" /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={territoryToDelete !== null} onOpenChange={(open) => !open && cancelDelete()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="flex items-start gap-2 text-amber-600">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>
                  Are you sure you want to delete this territory? This action cannot be undone, and any regions assigned
                  to this territory will become unassigned.
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
